import { BulkRecognitionApprovalResult, getError, useToast } from '@aslanpay/portal-common-lib';
import { useCallback } from 'react';

import useRecognition from 'app/hooks/useRecognition';

// TXN-80000 is what wallet-manager returns now; GEN-10409 is kept for backends not yet on that version.
// Only TXN-80000 is read from a bulk result, where GEN-10409 means the recognition is no longer pending.
const INSUFFICIENT_BALANCE_CODES = ['TXN-80000', 'GEN-10409'];
const BULK_INSUFFICIENT_BALANCE_CODE = 'TXN-80000';

const INSUFFICIENT_BALANCE_TITLE = 'Insufficient balance';
const INSUFFICIENT_BALANCE_DESCRIPTION =
  'The source pot does not have enough points to cover this recognition. Refresh and try again.';
const GENERIC_FAILURE_MESSAGE = 'Something went wrong. Please try again.';

// How many distinct failure messages the toast lists before summarising the rest.
const MAX_LISTED_FAILURE_MESSAGES = 3;

const failureMessageOf = ({ errorCode, errorMessage }: BulkRecognitionApprovalResult) => {
  if (errorCode === BULK_INSUFFICIENT_BALANCE_CODE) {
    return INSUFFICIENT_BALANCE_DESCRIPTION;
  }

  return errorMessage || GENERIC_FAILURE_MESSAGE;
};

const describeFailures = (failed: BulkRecognitionApprovalResult[]) => {
  const messages = [...new Set(failed.map(failureMessageOf))];
  const listed = messages.slice(0, MAX_LISTED_FAILURE_MESSAGES).join(' ');
  const remaining = messages.length - MAX_LISTED_FAILURE_MESSAGES;

  if (remaining > 0) {
    return `${listed} (+${remaining} more)`;
  }

  return listed;
};

// Approves one recognition with the single approve request, and more than one with the bulk
// request, then tells the user what happened. Resolves to true when the confirm step can close.
function useApproveSelectedRecognitions() {
  const toast = useToast();

  const { mutateAsync: approveRecognition, isPending: isApprovingOne } =
    useRecognition.useApproveRecognition();
  const { mutateAsync: approveRecognitions, isPending: isApprovingMany } =
    useRecognition.useApproveRecognitions();

  const approveOne = useCallback(
    async (recognitionId: string) => {
      try {
        await approveRecognition(recognitionId);
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Recognition approved',
          description: 'The sender has been notified',
        });
        return true;
      } catch (error) {
        const isInsufficientBalanceError =
          error instanceof Error && INSUFFICIENT_BALANCE_CODES.includes(getError(error).code);

        if (isInsufficientBalanceError) {
          toast({
            position: 'top-right',
            status: 'error',
            title: INSUFFICIENT_BALANCE_TITLE,
            description: INSUFFICIENT_BALANCE_DESCRIPTION,
          });
          return false;
        }

        toast({ position: 'top-right', status: 'error', title: 'Failed to approve recognition' });
        return false;
      }
    },
    [approveRecognition, toast]
  );

  const approveMany = useCallback(
    async (recognitionIds: string[]) => {
      const results = await approveRecognitions(recognitionIds);
      const queuedCount = results.filter(({ result }) => result === 'QUEUED').length;
      const failed = results.filter(({ result }) => result === 'FAILED');

      if (queuedCount > 0) {
        toast({
          position: 'top-right',
          status: 'info',
          title:
            queuedCount > 1
              ? `${queuedCount} approvals are being processed`
              : '1 approval is being processed',
          description:
            queuedCount > 1
              ? 'They show as processing until the points have been paid'
              : 'It shows as processing until the points have been paid',
        });
      }

      if (failed.length > 0) {
        toast({
          position: 'top-right',
          status: 'error',
          title:
            failed.length > 1
              ? `${failed.length} recognitions could not be approved`
              : '1 recognition could not be approved',
          description: describeFailures(failed),
        });
      }

      return true;
    },
    [approveRecognitions, toast]
  );

  const approve = useCallback(
    (recognitionIds: string[]) => {
      if (recognitionIds.length > 1) {
        return approveMany(recognitionIds);
      }

      return approveOne(recognitionIds[0]);
    },
    [approveMany, approveOne]
  );

  return { approve, isApproving: isApprovingOne || isApprovingMany };
}

export default useApproveSelectedRecognitions;
